import React, { useState, useEffect, useCallback } from 'react';
import { vehicleExpenseAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const STATUS_BADGES = {
  DRAFT: { label: 'Draft', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  SUBMITTED: { label: 'Pending Finance Action', bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  CORRECTION_REQUESTED: { label: 'Correction Requested', bg: '#ffedd5', color: '#c2410c', border: '#fed7aa' },
  APPROVED: { label: 'Approved & Released', bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  REJECTED: { label: 'Rejected', bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  COMPLETED: { label: 'Completed & Settled', bg: '#dcfce7', color: '#15803d', border: '#86efac' },
};

const TransportFinanceExpensesTab = ({ user }) => {
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState({
    total_requested: 0,
    total_approved: 0,
    pending_count: 0,
    completed_count: 0,
    status_counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Verification Modal State
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [actionChoice, setActionChoice] = useState('APPROVE'); // APPROVE | REJECT | REQUEST_CORRECTION
  const [approvedAmount, setApprovedAmount] = useState('');
  const [financeRemarks, setFinanceRemarks] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'ALL') params.status = statusFilter;
      if (search) params.search = search;
      if (dateFilter) params.date = dateFilter;

      const [data, statsData] = await Promise.all([
        vehicleExpenseAPI.getExpenses(params),
        vehicleExpenseAPI.getStats(),
      ]);

      setExpenses(Array.isArray(data) ? data : data.results || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch finance transport expenses:', err);
      setError('Failed to load transport expense requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, dateFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleOpenVerifyModal = (exp) => {
    setSelectedExpense(exp);
    setActionChoice('APPROVE');
    setApprovedAmount(exp.requested_amount);
    setFinanceRemarks('');
  };

  const handleProcessFinanceAction = async (e) => {
    e.preventDefault();

    if (actionChoice === 'APPROVE') {
      if (!approvedAmount || parseFloat(approvedAmount) <= 0) {
        notify.error('Please enter a valid approved amount.');
        return;
      }
    } else if (actionChoice === 'REJECT' || actionChoice === 'REQUEST_CORRECTION') {
      if (!financeRemarks.trim()) {
        notify.error('Please enter remarks/reason for rejection or correction request.');
        return;
      }
    }

    setProcessingAction(true);
    try {
      const payload = {
        action: actionChoice,
        approved_amount: actionChoice === 'APPROVE' ? approvedAmount : 0,
        finance_remarks: financeRemarks,
        rejection_reason: actionChoice === 'REJECT' ? financeRemarks : '',
        correction_reason: actionChoice === 'REQUEST_CORRECTION' ? financeRemarks : '',
      };

      const res = await vehicleExpenseAPI.financeAction(selectedExpense.id, payload);
      const reqCode = res.request_id || res.request_number || selectedExpense.request_id || '';
      setSuccessMsg(`Action successfully recorded for ${reqCode}. Status is now ${res.status_display || res.status}.`);
      setSelectedExpense(null);
      fetchExpenses();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Finance action error:', err);
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Failed to process action.';
      notify.success(msg);
    } finally {
      setProcessingAction(false);
    }
  };

  const isUserCreator = selectedExpense && user && selectedExpense.created_by === user.id && !user.is_superuser;

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
          🚌 Vehicle Maintenance & Expense Approvals
        </h2>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Verify supporting bills & quotations submitted by Transport Department before approving and releasing funds.
        </div>
      </div>

      {successMsg && (
        <div style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>✓</span> <span>{successMsg}</span>
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 240px' }}>
          <input
            type="text"
            placeholder="🔍 Search request no, vehicle reg, vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid #2563eb',
              background: '#eff6ff',
              color: '#1d4ed8',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="SUBMITTED">Pending Approval</option>
            <option value="APPROVED">Approved & Released</option>
            <option value="CORRECTION_REQUESTED">Correction Requested</option>
            <option value="REJECTED">Rejected</option>
            <option value="COMPLETED">Completed</option>
            <option value="ALL">All Requests</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>📅 Date:</span>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: 13,
              background: '#fff',
            }}
            title="Filter by Date"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #cbd5e1',
                background: '#f8fafc',
                color: '#64748b',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Clear Date
            </button>
          )}
        </div>
      </div>

      {/* Requests Table */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
            ⏳ Loading transport expense requests...
          </div>
        ) : error ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#ef4444', fontSize: 14 }}>
            ⚠️ {error}
          </div>
        ) : expenses.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No Requests Matching Filter</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>No pending transport expense verification requests at this time.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                  <th style={{ padding: '12px 16px' }}>Request ID / Date</th>
                  <th style={{ padding: '12px 16px' }}>Vehicle</th>
                  <th style={{ padding: '12px 16px' }}>Category & Vendor</th>
                  <th style={{ padding: '12px 16px' }}>Requested (₹)</th>
                  <th style={{ padding: '12px 16px' }}>Approved (₹)</th>
                  <th style={{ padding: '12px 16px' }}>Requested By</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Submitted Docs</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => {
                  const badge = STATUS_BADGES[exp.status] || { label: exp.status, bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
                  const createdDate = new Date(exp.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                  const reqCode = exp.request_id || exp.request_number || `REQ-${exp.id}`;

                  // Extract URLs from serializer response
                  const quoteUrl = exp.quotation_document_url || exp.quotation_document;
                  const billUrl = exp.supporting_bill_document_url || exp.supporting_bill_document;
                  const finalInvoiceUrl = exp.final_paid_invoice_url || exp.final_paid_invoice;
                  const renewedDocUrl = exp.renewed_vehicle_document_url || exp.renewed_vehicle_document;

                  return (
                    <tr key={exp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{reqCode}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{createdDate}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>🚘 {exp.vehicle_registration_number}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {exp.vehicle_details?.model || exp.vehicle_details?.make || exp.vehicle_model}
                          {(exp.odo_reading || exp.odometer_reading) ? ` • 📈 ODO: ${(exp.odo_reading || exp.odometer_reading).toLocaleString()} KM` : ''}
                        </div>
                        {exp.expense_category === 'FUEL' && (
                          <div style={{ marginTop: 4 }}>
                            {exp.fuel_km_difference != null ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                                ⛽ +{exp.fuel_km_difference.toLocaleString()} KM since refill
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600, background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
                                ⛽ Baseline Refill Log
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#334155' }}>{exp.expense_category_display || exp.category_display || exp.expense_category}</div>
                        {exp.vendor_name && <div style={{ fontSize: 11, color: '#64748b' }}>Vendor: {exp.vendor_name}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                        ₹{parseFloat(exp.requested_amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: exp.approved_amount ? '#15803d' : '#94a3b8' }}>
                        {exp.approved_amount ? `₹${parseFloat(exp.approved_amount).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#334155' }}>{exp.created_by_name || 'Transport Mgr'}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>Transport Dept</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
                          {quoteUrl && (
                            <a href={quoteUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                              📄 Quotation
                            </a>
                          )}
                          {billUrl && (
                            <a href={billUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                              🧾 Initial Bill
                            </a>
                          )}
                          {finalInvoiceUrl && (
                            <a href={finalInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: '#16a34a', fontWeight: 700, textDecoration: 'underline' }}>
                              ✅ Final Invoice
                            </a>
                          )}
                          {renewedDocUrl && (
                            <a href={renewedDocUrl} target="_blank" rel="noreferrer" style={{ color: '#0d9488', fontWeight: 700, textDecoration: 'underline' }}>
                              🛡️ Renewed Doc
                            </a>
                          )}
                          {!quoteUrl && !billUrl && !finalInvoiceUrl && !renewedDocUrl && (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No files</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleOpenVerifyModal(exp)}
                          style={{
                            padding: '6px 12px',
                            fontSize: 12,
                            borderRadius: 8,
                            border: exp.status === 'SUBMITTED' ? '1px solid #2563eb' : '1px solid #cbd5e1',
                            background: exp.status === 'SUBMITTED' ? '#2563eb' : '#f8fafc',
                            color: exp.status === 'SUBMITTED' ? '#ffffff' : '#334155',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {exp.status === 'SUBMITTED' ? '🔍 Verify & Approve' : '👁️ View & Audit'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FINANCE VERIFICATION & ACTION MODAL */}
      {selectedExpense && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  🔍 Finance Verification: {selectedExpense.request_id || selectedExpense.request_number}
                </h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Requested by: <strong>{selectedExpense.created_by_name || 'Transport Manager'}</strong> &bull; Date: {new Date(selectedExpense.created_at).toLocaleDateString('en-IN')}
                </div>
              </div>
              <button onClick={() => setSelectedExpense(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            {/* Vehicle & Request Overview Box */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                <div><span style={{ color: '#64748b' }}>Vehicle:</span> <strong>🚘 {selectedExpense.vehicle_registration_number}</strong></div>
                <div><span style={{ color: '#64748b' }}>Model/Make:</span> <strong>{selectedExpense.vehicle_details?.model || selectedExpense.vehicle_details?.make || selectedExpense.vehicle_model}</strong></div>
                <div><span style={{ color: '#64748b' }}>Category:</span> <strong>{selectedExpense.expense_category_display || selectedExpense.category_display || selectedExpense.expense_category}</strong></div>
                <div><span style={{ color: '#64748b' }}>Vendor Name:</span> <strong>{selectedExpense.vendor_name || 'N/A'}</strong></div>
                <div style={{ gridColumn: 'span 2' }}>
                  <span style={{ color: '#64748b' }}>Requested Amount:</span> <strong style={{ fontSize: 16, color: '#0f172a', marginLeft: 4 }}>₹{parseFloat(selectedExpense.requested_amount).toLocaleString('en-IN')}</strong>
                </div>
              </div>

              {(selectedExpense.expense_description || selectedExpense.description) && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0', fontSize: 13, color: '#334155' }}>
                  <strong>Scope of Work / Description:</strong> {selectedExpense.expense_description || selectedExpense.description}
                </div>
              )}
            </div>

            {/* FUEL EXPENSE ODO & MILEAGE VERIFICATION CARD */}
            {selectedExpense.expense_category === 'FUEL' && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
                  border: '1px solid #bfdbfe',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  boxShadow: '0 2px 6px rgba(37, 99, 235, 0.06)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #dbeafe', paddingBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>⛽</span>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e3a8a' }}>
                        Fuel Refill & Mileage Verification
                      </h4>
                      <div style={{ fontSize: 11, color: '#2563eb' }}>
                        Audited odometer interval between vehicle fuel refills for mileage & consumption verification
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '3px 10px',
                      borderRadius: 9999,
                      background: '#dbeafe',
                      color: '#1d4ed8',
                      border: '1px solid #93c5fd',
                    }}
                  >
                    Odometer Verification
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                  {/* Box 1: Current Refill ODO */}
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 10,
                      padding: 12,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Current Refill ODO
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>
                      {(selectedExpense.odo_reading || selectedExpense.current_fuel_odo || selectedExpense.odometer_reading) != null
                        ? `${(selectedExpense.odo_reading || selectedExpense.current_fuel_odo || selectedExpense.odometer_reading).toLocaleString()} KM`
                        : 'Not Recorded'}
                    </div>
                    <div style={{ fontSize: 11, color: '#2563eb', marginTop: 2, fontWeight: 600 }}>
                      📍 Recorded at latest fuel request
                    </div>
                  </div>

                  {/* Box 2: Last Recorded Refill ODO */}
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      padding: 12,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Last Recorded Refill ODO
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#334155', marginTop: 4 }}>
                      {selectedExpense.last_fuel_odo != null
                        ? `${selectedExpense.last_fuel_odo.toLocaleString()} KM`
                        : '—'}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {selectedExpense.last_fuel_odo != null ? (
                        <>From {selectedExpense.previous_fuel_expense_info?.last_request_id || selectedExpense.previous_fuel_expense_info?.request_id || 'Previous Refill'}</>
                      ) : (
                        'Initial baseline (first logged refill)'
                      )}
                    </div>
                  </div>

                  {/* Box 3: KM Difference Between Refills */}
                  <div
                    style={{
                      background: selectedExpense.fuel_km_difference != null
                        ? (selectedExpense.fuel_km_difference >= 0 ? '#f0fdf4' : '#fef2f2')
                        : '#f8fafc',
                      border: selectedExpense.fuel_km_difference != null
                        ? (selectedExpense.fuel_km_difference >= 0 ? '1px solid #86efac' : '1px solid #fca5a5')
                        : '1px solid #e2e8f0',
                      borderRadius: 10,
                      padding: 12,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: selectedExpense.fuel_km_difference != null && selectedExpense.fuel_km_difference < 0 ? '#991b1b' : '#166534',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      KM Traveled Between Refills
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 900,
                        color: selectedExpense.fuel_km_difference != null
                          ? (selectedExpense.fuel_km_difference >= 0 ? '#15803d' : '#dc2626')
                          : '#64748b',
                        marginTop: 4,
                      }}
                    >
                      {selectedExpense.fuel_km_difference != null
                        ? `${selectedExpense.fuel_km_difference >= 0 ? '+' : ''}${selectedExpense.fuel_km_difference.toLocaleString()} KM`
                        : 'First Refill'}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: selectedExpense.fuel_km_difference != null
                          ? (selectedExpense.fuel_km_difference >= 0 ? '#15803d' : '#b91c1c')
                          : '#64748b',
                        marginTop: 2,
                        fontWeight: 600,
                      }}
                    >
                      {selectedExpense.fuel_km_difference != null
                        ? (selectedExpense.fuel_km_difference >= 0 ? '✅ Verified distance driven' : '⚠️ Warning: ODO is lower than previous refill')
                        : 'Initial baseline for future refills'}
                    </div>
                  </div>
                </div>

                {/* Previous Fuel Refill Information Banner */}
                {(selectedExpense.previous_fuel_expense_info?.has_previous_refill || selectedExpense.last_fuel_odo != null) ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid #bfdbfe',
                      borderRadius: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: 12,
                      color: '#1e40af',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}
                  >
                    <div>
                      <strong>Prior Fuel Request:</strong> {selectedExpense.previous_fuel_expense_info?.last_request_id || selectedExpense.previous_fuel_expense_info?.request_id || `#${selectedExpense.previous_fuel_expense_info?.id || 'PREV'}`}
                      {(selectedExpense.previous_fuel_expense_info?.last_request_date || selectedExpense.previous_fuel_expense_info?.created_at) && (
                        <> on {new Date(selectedExpense.previous_fuel_expense_info?.last_request_date || selectedExpense.previous_fuel_expense_info?.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</>
                      )}
                      {(selectedExpense.previous_fuel_expense_info?.last_amount || selectedExpense.previous_fuel_expense_info?.requested_amount) && (
                        <> (₹{parseFloat(selectedExpense.previous_fuel_expense_info?.last_amount || selectedExpense.previous_fuel_expense_info?.requested_amount).toLocaleString('en-IN')})</>
                      )}
                    </div>
                    <div style={{ fontWeight: 700, color: '#15803d' }}>
                      Interval: +{selectedExpense.fuel_km_difference != null ? selectedExpense.fuel_km_difference.toLocaleString() : 0} KM
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '8px 12px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      border: '1px dashed #93c5fd',
                      borderRadius: 8,
                      fontSize: 12,
                      color: '#1e40af',
                    }}
                  >
                    ℹ️ This is the first logged fuel expense for <strong>{selectedExpense.vehicle_registration_number}</strong> with odometer tracking. The current reading will serve as the starting baseline for subsequent refills.
                  </div>
                )}
              </div>
            )}

            {/* Uploaded Documents Verification */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                📄 Submitted Supporting Documents
              </h4>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                {(selectedExpense.quotation_document_url || selectedExpense.quotation_document) ? (
                  <a
                    href={selectedExpense.quotation_document_url || selectedExpense.quotation_document}
                    target="_blank"
                    rel="noreferrer"
                    style={{ padding: '8px 14px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    📄 View Quotation / Estimate
                  </a>
                ) : (
                  <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No Quotation Document</div>
                )}

                {(selectedExpense.supporting_bill_document_url || selectedExpense.supporting_bill_document) ? (
                  <a
                    href={selectedExpense.supporting_bill_document_url || selectedExpense.supporting_bill_document}
                    target="_blank"
                    rel="noreferrer"
                    style={{ padding: '8px 14px', borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    🧾 View Initial Bill / Invoice
                  </a>
                ) : (
                  <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No Initial Bill Document</div>
                )}

                {(selectedExpense.final_paid_invoice_url || selectedExpense.final_paid_invoice) && (
                  <a
                    href={selectedExpense.final_paid_invoice_url || selectedExpense.final_paid_invoice}
                    target="_blank"
                    rel="noreferrer"
                    style={{ padding: '8px 14px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    ✅ View Paid Final Invoice
                  </a>
                )}
              </div>
            </div>

            {/* Self Approval Warning */}
            {isUserCreator && (
              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: 12, marginBottom: 16, color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
                ⚠️ You created this expense request. The Transport Manager who created the request cannot approve their own expense request. Another Finance Officer or Admin must approve it.
              </div>
            )}

            {/* Action Form */}
            <form onSubmit={handleProcessFinanceAction}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
                  Select Finance Action
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setActionChoice('APPROVE')}
                    style={{
                      padding: '10px',
                      borderRadius: 10,
                      border: actionChoice === 'APPROVE' ? '2px solid #16a34a' : '1px solid #cbd5e1',
                      background: actionChoice === 'APPROVE' ? '#dcfce7' : '#f8fafc',
                      color: actionChoice === 'APPROVE' ? '#15803d' : '#475569',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    ✅ Approve & Release
                  </button>

                  <button
                    type="button"
                    onClick={() => setActionChoice('REQUEST_CORRECTION')}
                    style={{
                      padding: '10px',
                      borderRadius: 10,
                      border: actionChoice === 'REQUEST_CORRECTION' ? '2px solid #ea580c' : '1px solid #cbd5e1',
                      background: actionChoice === 'REQUEST_CORRECTION' ? '#ffedd5' : '#f8fafc',
                      color: actionChoice === 'REQUEST_CORRECTION' ? '#c2410c' : '#475569',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    📝 Request Correction
                  </button>

                  <button
                    type="button"
                    onClick={() => setActionChoice('REJECT')}
                    style={{
                      padding: '10px',
                      borderRadius: 10,
                      border: actionChoice === 'REJECT' ? '2px solid #dc2626' : '1px solid #cbd5e1',
                      background: actionChoice === 'REJECT' ? '#fee2e2' : '#f8fafc',
                      color: actionChoice === 'REJECT' ? '#b91c1c' : '#475569',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    ❌ Reject Request
                  </button>
                </div>
              </div>

              {actionChoice === 'APPROVE' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 4 }}>
                    Approved Amount (₹) to Release *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #86efac', fontSize: 14, fontWeight: 700, color: '#15803d' }}
                  />
                  <div style={{ fontSize: 11, color: '#166534', marginTop: 4 }}>
                    Requested: ₹{parseFloat(selectedExpense.requested_amount).toLocaleString('en-IN')}. You can adjust approved amount if partial sanction is needed.
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Finance Remarks / Reason {actionChoice !== 'APPROVE' && '*'}
                </label>
                <textarea
                  rows={3}
                  placeholder={actionChoice === 'APPROVE' ? 'Optional verification notes or voucher reference...' : 'Explain why correction or rejection is required...'}
                  value={financeRemarks}
                  onChange={(e) => setFinanceRemarks(e.target.value)}
                  required={actionChoice !== 'APPROVE'}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setSelectedExpense(null)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer' }}
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={processingAction || isUserCreator}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 8,
                    border: 'none',
                    background: isUserCreator ? '#94a3b8' : actionChoice === 'APPROVE' ? '#16a34a' : actionChoice === 'REQUEST_CORRECTION' ? '#ea580c' : '#dc2626',
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isUserCreator ? 'not-allowed' : 'pointer',
                  }}
                >
                  {processingAction ? 'Processing...' : actionChoice === 'APPROVE' ? 'Confirm Approval & Release Funds' : actionChoice === 'REQUEST_CORRECTION' ? 'Send Correction Request' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportFinanceExpensesTab;
